import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/context";
import {
  contactRequestErrorMessage,
  decideContactRequest,
  fetchContactRequests,
  fetchContacts,
  removeContact,
  sendContactRequest,
  userLabel,
  type Contact,
  type ContactRequest,
} from "../contacts/api";
import { messageErrorMessage, sendMessage } from "../messages/api";

// /contacts — three sections:
//   1. Add new (search by @handle or email, optional message).
//   2. Incoming requests + outgoing requests (only pending).
//   3. Accepted contacts list.

export function Contacts() {
  const { state } = useAuth();
  const accessToken =
    (state.status === "authenticated" && state.session.access_token) ||
    (state.status === "offline" && state.session.access_token) ||
    null;

  const [contacts, setContacts] = useState<Contact[]>([]);
  const [incoming, setIncoming] = useState<ContactRequest[]>([]);
  const [outgoing, setOutgoing] = useState<ContactRequest[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const [c, inc, out] = await Promise.all([
        fetchContacts(accessToken),
        fetchContactRequests(accessToken, "incoming"),
        fetchContactRequests(accessToken, "outgoing"),
      ]);
      setContacts(c);
      setIncoming(inc);
      setOutgoing(out);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (!accessToken) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-10 text-sm text-slate-500 dark:text-slate-400">
        Sign in to see your contacts.
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-2xl px-6 py-8">
      <div className="mb-6 flex items-baseline justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Contacts</h1>
        <Link
          to="/"
          className="text-sm text-slate-500 underline hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          Back
        </Link>
      </div>

      <AddContactForm token={accessToken} onAdded={() => void refresh()} />

      {error && (
        <p className="mt-3 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-300">
          {error}
        </p>
      )}

      {incoming.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Incoming requests
          </h2>
          <ul className="space-y-1.5">
            {incoming.map((r) => (
              <RequestRow
                key={r.id}
                req={r}
                token={accessToken}
                onChanged={() => void refresh()}
              />
            ))}
          </ul>
        </section>
      )}

      {outgoing.length > 0 && (
        <section className="mt-6">
          <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
            Sent · pending
          </h2>
          <ul className="space-y-1.5">
            {outgoing.map((r) => (
              <RequestRow
                key={r.id}
                req={r}
                token={accessToken}
                onChanged={() => void refresh()}
              />
            ))}
          </ul>
        </section>
      )}

      <section className="mt-6">
        <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Contacts
          {contacts.length > 0 && (
            <span className="ml-2 text-slate-400 dark:text-slate-500">{contacts.length}</span>
          )}
        </h2>
        {contacts.length === 0 ? (
          <p className="text-sm italic text-slate-400 dark:text-slate-500">
            {loading ? "Loading…" : "No contacts yet."}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {contacts.map((c) => (
              <ContactRow
                key={c.userId}
                contact={c}
                token={accessToken}
                onChanged={() => void refresh()}
              />
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

function AddContactForm({
  token,
  onAdded,
}: {
  token: string;
  onAdded: () => void;
}) {
  const [target, setTarget] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const submit = async () => {
    if (!target.trim()) return;
    setSending(true);
    setError(null);
    setSent(false);
    try {
      await sendContactRequest(token, target.trim(), message.trim() || undefined);
      setTarget("");
      setMessage("");
      setSent(true);
      onAdded();
      setTimeout(() => setSent(false), 3000);
    } catch (err) {
      const code = err instanceof Error ? err.message : String(err);
      setError(contactRequestErrorMessage(code));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
      <label className="block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Add a contact
      </label>
      <div className="mt-1 flex gap-2">
        <input
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          placeholder="@handle or email"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          className="flex-1 rounded border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800"
          onKeyDown={(e) => {
            if (e.key === "Enter") void submit();
          }}
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={sending || !target.trim()}
          className="rounded bg-slate-900 px-3 py-1 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
        >
          {sending ? "Sending…" : "Send request"}
        </button>
      </div>
      <input
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        placeholder="Optional message (160 chars)"
        maxLength={160}
        className="mt-2 w-full rounded border border-slate-200 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
      />
      {error && <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{error}</p>}
      {sent && (
        <p className="mt-2 text-xs text-emerald-700 dark:text-emerald-300">Request sent.</p>
      )}
    </section>
  );
}

function RequestRow({
  req,
  token,
  onChanged,
}: {
  req: ContactRequest;
  token: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (action: "accept" | "decline" | "revoke") => {
    setBusy(true);
    setError(null);
    try {
      await decideContactRequest(token, req.id, action);
      onChanged();
    } catch (err) {
      setError(
        contactRequestErrorMessage(
          err instanceof Error ? err.message : String(err),
        ),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="flex items-start gap-3 rounded border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 dark:text-slate-100">{userLabel(req.other)}</div>
        <div className="text-[11px] text-slate-500 dark:text-slate-400">{req.other.email}</div>
        {req.message && (
          <div className="mt-1 text-xs italic text-slate-600 dark:text-slate-300">
            "{req.message}"
          </div>
        )}
        {error && (
          <div className="mt-1 text-xs text-rose-600 dark:text-rose-400">{error}</div>
        )}
      </div>
      <div className="flex shrink-0 gap-1">
        {req.direction === "incoming" ? (
          <>
            <button
              type="button"
              onClick={() => void act("accept")}
              disabled={busy}
              className="rounded bg-slate-900 px-2 py-0.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
            >
              Accept
            </button>
            <button
              type="button"
              onClick={() => void act("decline")}
              disabled={busy}
              className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Decline
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={() => void act("revoke")}
            disabled={busy}
            className="rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-500 hover:bg-slate-100 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
        )}
      </div>
    </li>
  );
}

function ContactRow({
  contact,
  token,
  onChanged,
}: {
  contact: Contact;
  token: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [composing, setComposing] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState<number | null>(null);

  const remove = async () => {
    if (
      !confirm(
        `Remove ${userLabel(contact)} from your contacts? They'll no longer appear in pickers.`,
      )
    )
      return;
    setBusy(true);
    try {
      await removeContact(token, contact.userId);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    setSendError(null);
    try {
      await sendMessage(token, contact.userId, body);
      setDraft("");
      setSentAt(Date.now());
      setComposing(false);
      setTimeout(() => setSentAt(null), 3000);
    } catch (err) {
      setSendError(
        messageErrorMessage(err instanceof Error ? err.message : String(err)),
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <li className="group rounded border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 dark:text-slate-100">{userLabel(contact)}</div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400">{contact.email}</div>
        </div>
        <button
          type="button"
          onClick={() => setComposing((v) => !v)}
          className="shrink-0 rounded border border-slate-300 bg-white px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          {composing ? "Cancel" : "Message"}
        </button>
        <button
          type="button"
          onClick={() => void remove()}
          disabled={busy}
          className="shrink-0 rounded px-1.5 py-0.5 text-xs text-rose-600 opacity-0 hover:bg-rose-50 hover:text-rose-700 group-hover:opacity-100 disabled:opacity-50 dark:text-rose-400 dark:hover:bg-rose-950 dark:hover:text-rose-300"
        >
          Remove
        </button>
      </div>
      {sentAt && Date.now() - sentAt < 3000 && (
        <p className="mt-1 text-[11px] text-emerald-700 dark:text-emerald-300">Sent.</p>
      )}
      {composing && (
        <div className="mt-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={`Message ${userLabel(contact)}…`}
            rows={3}
            maxLength={2000}
            className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-800"
            onKeyDown={(e) => {
              // Cmd/Ctrl+Enter to send — matches the convention from
              // Slack / GitHub / etc.
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                e.preventDefault();
                void send();
              }
            }}
          />
          <div className="mt-1 flex items-center gap-2">
            <button
              type="button"
              onClick={() => void send()}
              disabled={sending || !draft.trim()}
              className="rounded bg-slate-900 px-2 py-0.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-slate-200"
            >
              {sending ? "Sending…" : "Send"}
            </button>
            <span className="text-[10px] text-slate-400 dark:text-slate-500">
              ⌘/Ctrl+Enter to send · {draft.length}/2000
            </span>
            {sendError && (
              <span className="text-[11px] text-rose-600 dark:text-rose-400">{sendError}</span>
            )}
          </div>
        </div>
      )}
    </li>
  );
}
